import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import {
  filterSubscriptionCustomers,
  legacyCustomerApi,
  subscriptionAdminApi,
  getApiErrorMessage,
} from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import { openRazorpayOrderCheckout, formatInr } from '../subscriptionAdmin/razorpay';
import { getPaymentStatusLabel } from '../utils/paymentRefundDisplay';

const ELIGIBLE_STATUSES = ['approved', 'pending_payment', 'active', 'renewal_pending'];

function contractRowId(contract) {
  return String(contract?.id || contract?._id || '');
}

function isOfflineContract(contract) {
  return contract?.billing?.paymentChannel === 'offline' || contract?.paymentChannel === 'offline';
}

function contractAmount(contract) {
  return contract?.billing?.amount ?? contract?.amount;
}

function paymentListItems(data) {
  if (Array.isArray(data)) return data;
  return data?.items || [];
}

function buildPaymentSuccessMessage(confirmResult) {
  if (confirmResult?.message) return confirmResult.message;
  if (confirmResult?.mandateAuthRefunded) {
    return 'Autopay saved. Verification charge refunded automatically.';
  }
  if (confirmResult?.mode === 'already_paid' || confirmResult?.duplicate) {
    return confirmResult.message || 'Payment already completed for this billing cycle.';
  }
  return 'Payment confirmed. Check Step 5 — Subscriptions.';
}

export default function PaymentSetupPage() {
  const [customers, setCustomers] = useState([]);
  const [userId, setUserId] = useState('');
  const [contractId, setContractId] = useState('');
  const [setup, setSetup] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canPay = canWrite('payments');

  const loadCustomers = useCallback(async () => {
    try {
      const users = await legacyCustomerApi.listUsers();
      setCustomers(filterSubscriptionCustomers(users));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const loadSetup = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [data, paymentList] = await Promise.all([
        subscriptionAdminApi.getPaymentSetup(userId),
        subscriptionAdminApi.listPayments({ userId }).catch(() => ({ items: [] })),
      ]);
      setSetup(data);
      setPayments(paymentListItems(paymentList));
      const contracts = (data?.contracts || []).filter((c) => ELIGIBLE_STATUSES.includes(c.status));
      setContractId((prev) => {
        if (prev && contracts.some((c) => contractRowId(c) === prev)) return prev;
        return contractRowId(contracts[0]) || '';
      });
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setSetup(null);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadSetup();
    else {
      setSetup(null);
      setPayments([]);
    }
  }, [userId, loadSetup]);

  const selectedContract = (setup?.contracts || []).find((c) => contractRowId(c) === contractId);
  const selectedIsOffline = isOfflineContract(selectedContract);

  const initiatePayment = async () => {
    if (!userId || !contractId) {
      setSnackbar({ open: true, type: 'error', message: 'Select customer and contract.' });
      return;
    }
    if (selectedIsOffline) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Offline contracts use Record payment / Activate offline on the Contracts page.',
      });
      return;
    }

    setPaying(true);
    try {
      const data = await subscriptionAdminApi.initiatePaymentSetup(userId, { contractId });

      if (data.mode === 'free_month') {
        setSnackbar({
          open: true,
          type: 'success',
          message: data.message || 'Free month applied — no checkout required.',
        });
        loadSetup();
        return;
      }

      if (data.mode === 'already_paid') {
        setSnackbar({
          open: true,
          type: 'success',
          message: buildPaymentSuccessMessage(data),
        });
        loadSetup();
        return;
      }

      if (data.mode !== 'razorpay_order' && data.mode !== 'mandate_setup') {
        throw new Error(data.message || 'Unsupported payment mode from server.');
      }

      const confirmResult = await openRazorpayOrderCheckout({
        razorpayKeyId: data.razorpayKeyId,
        order: data.order,
        customerId: data.customerId,
        charge: data.charge,
        checkoutOptions: data.checkoutOptions,
        description:
          data.mode === 'mandate_setup'
            ? 'Autopay verification (refunded automatically)'
            : `Billing cycle ${data.billingCycleKey || ''}`,
        prefill: {},
        onConfirm: (razorpayPayload) =>
          subscriptionAdminApi.confirmPaymentSetup(userId, razorpayPayload),
      });

      setSnackbar({
        open: true,
        type: 'success',
        message: buildPaymentSuccessMessage(confirmResult),
      });
      loadSetup();
    } catch (err) {
      if (err.message !== 'Payment cancelled') {
        setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      }
    } finally {
      setPaying(false);
    }
  };

  const eligibleContracts = (setup?.contracts || []).filter((c) =>
    ELIGIBLE_STATUSES.includes(c.status)
  );

  return (
    <PageShell
      title="Step 4 — Payment setup"
      subtitle="GET /users/:userId/payment-setup · POST .../initiate · POST .../confirm"
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Online contracts: Razorpay checkout (including autopay mandate setup for free-month plans).
        Offline contracts: use <strong>Activate offline</strong> or <strong>Record payment</strong> on Step 2 — Contracts.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            select
            label="Customer"
            value={userId}
            onChange={(e) => { setUserId(e.target.value); setContractId(''); }}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="">Select customer…</MenuItem>
            {customers.map((c) => (
              <MenuItem key={c._id} value={c._id}>{c.email}</MenuItem>
            ))}
          </TextField>
          {userId && (
            <TextField
              select
              label="Contract"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="">Select contract…</MenuItem>
              {eligibleContracts.map((c) => {
                const id = contractRowId(c);
                return (
                  <MenuItem key={id} value={id}>
                    {c.contractNumber || id.slice(-8)} — {c.status}
                    {isOfflineContract(c) ? ' (offline)' : ''} — {formatInr(contractAmount(c))}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          {canPay && userId && contractId && !selectedIsOffline && (
            <Button variant="contained" onClick={initiatePayment} disabled={paying}>
              {paying ? 'Processing…' : 'Initiate payment'}
            </Button>
          )}
          {selectedIsOffline && (
            <Chip label="Offline — use Contracts page" color="warning" size="small" />
          )}
        </CardContent>
      </Card>

      {loading && <CircularProgress />}

      {setup && !loading && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '1fr 1fr' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Contracts</Typography>
              <List dense>
                {(setup.contracts || []).map((c) => {
                  const id = contractRowId(c);
                  return (
                    <ListItem key={id}>
                      <ListItemText
                        primary={`${c.contractNumber || id.slice(-8)} — ${c.status}`}
                        secondary={`${c.billingFlow || c.billing?.cycle || 'monthly'} · ${formatInr(contractAmount(c))}${isOfflineContract(c) ? ' · offline' : ''}`}
                      />
                      <Chip label={c.status} size="small" />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Pending payments</Typography>
              <List dense>
                {(setup.pendingPayments || setup.payments || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">None pending.</Typography>
                )}
                {(setup.pendingPayments || setup.payments || []).map((p) => (
                  <ListItem key={p._id}>
                    <ListItemText
                      primary={getPaymentStatusLabel(p)}
                      secondary={formatInr(p.amount || p.charge?.grossAmount)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      )}

      {payments.length > 0 && (
        <Box sx={{ mt: 3, overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Payment ledger (GET /payments)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Contract</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p._id}>
                  <TableCell>{getPaymentStatusLabel(p)}</TableCell>
                  <TableCell>{formatInr(p.amount || p.grossAmount)}</TableCell>
                  <TableCell>{String(p.contractId || '').slice(-8) || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
